/* eslint no-console: ["error", { allow: ["error"] }] */
import { createAsyncThunk } from "@reduxjs/toolkit";
import GeoJSON from "geojson";
import { AxiosError, AxiosResponse, isAxiosError } from "axios";
import isEqual from "lodash.isequal";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { aggregatePagedResponse } from "@iventis/api-helpers/src/api-helpers";
import { UpdateMultipleMapObjects } from "@iventis/domain-model/model/updateMultipleMapObjects";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import type { Map } from "@iventis/domain-model/model/map";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldValueMode } from "@iventis/domain-model/model/dataFieldValueMode";
import { TypedFeature } from "@iventis/map-engine/src/types/internal";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { DigitalTwinConnectionError } from "@iventis/digital-twin/src/digitial-twin-types";
import { Terrain } from "@iventis/domain-model/model/terrain";
import { Asset } from "@iventis/domain-model/model/asset";
import { SavedMapView } from "@iventis/domain-model/model/savedMapView";
import { GeometryType } from "@iventis/domain-model/model/geometryType";
import { MapComment } from "@iventis/domain-model/model/mapComment";
import qs from "qs";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import { ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { convertDateDatafieldValuesToUTC, convertDateDatafieldValuesToLocal, uuidOnlyObjectEntries, parseDataFieldValuesWithRepeatedTimeRange } from "@iventis/utilities";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { parseISO } from "date-fns";
import { CreateMapRequestPayload, DomainLayer, MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { OptionalExceptFor } from "@iventis/types/useful.types";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { mappingApi, assetsApi } from "@iventis/api/src/api";
import { RelayTimetableConfig } from "@iventis/domain-model/model/relayTimetableConfig";
import { createRelayTimetableApiFunctions } from "@iventis/relay-timetable-integration";
import { ResponseError } from "@iventis/api/src/axios.types";
import { DEFAULT_COMMENT_ICON_VALUE, commentsLayerId } from "@iventis/map-engine";
import { incrementMonitoringCount, layerCreationMonitoringMetric } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { LocalGeoJsonObject, MapCommentProperties, MapObjectProperties, SelectedMapObject } from "@iventis/map-types";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { AuthState } from "@iventis/permissions/src/auth.slice";
import { doesDataFieldHaveDefaultValue } from "@iventis/datafield-editor/src/attribute-default-value-helpers";
import { map } from "./map.slice";
import { SidebarOrderUpdate } from "../sidebar-order-helpers";
import { deleteLayerDataField, postLayerDataField } from "../services/data-field-service";
import { patchLayers, postLayer } from "../services/layer-service";
import { routeWaypointService } from "../services/route-waypoint-service";
import { eventStream } from "../event-stream";

type RootState = { mapReducer: MapGlobalState; auth: AuthState };

export const createMapRequest = createAsyncThunk<Map, CreateMapRequestPayload>("map/createMapRequest", async (map, { rejectWithValue }) => {
    try {
        const response = await mappingApi.post<Map>(
            "/maps",
            { type: NodeType.Map, ...map },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.plan) }),
                    message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.plan) }),
                },
            }
        );
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const createLayerRequest = createAsyncThunk<MapLayer, MapLayer, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/createLayerRequest",
    async (layer, { rejectWithValue, getState }) => {
        try {
            const response = await postLayer(layer, getState().mapReducer.mapModule.layers.value);
            layerCreationMonitoringMetric(layer.styleType, { resourceId: getState().mapReducer.mapModule.mapId });
            return response;
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            console.error("Error", error);
            return rejectWithValue(error.response.data);
        }
    }
);

export const updateMap = createAsyncThunk("Map/updateMap", async (options: { map: Partial<Map>; skipSave?: boolean }, { rejectWithValue }) => {
    try {
        if (options.skipSave) {
            return { map: options.map };
        }
        const response = await mappingApi.patch<Map>(`/maps/${options.map.id}`, options.map, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.plan) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.plan) }),
            },
        });
        return { map: response.data };
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export type MapLayerPatchType = Partial<Omit<MapLayer, "dataFields">> & Pick<MapLayer, "id">;

export const patchMapLayers = createAsyncThunk<DomainLayer[], MapLayerPatchType[], { state: RootState }>(
    "Map/patchMapLayers",
    async (mapLayers: MapLayerPatchType[], { rejectWithValue, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;

            await patchLayers(mapId, mapLayers);

            return getState().mapReducer.mapModule.layers.value.reduce<DomainLayer[]>((cum, lyr) => {
                const layer = mapLayers.find(({ id }) => lyr.id === id);
                return layer ? [...cum, { ...lyr, ...layer }] : cum;
            }, []);
        } catch (err) {
            const error: AxiosError<ResponseError> = err;
            // eslint-disable-next-line no-console
            console.error("Error", error);
            return rejectWithValue(error.response.data);
        }
    }
);
export const createLayerDataFieldThunk = createAsyncThunk<DataField, { dataField: DataField; layerId: string }, { state: RootState }>(
    "Map/createLayerDataField",
    async (request, { rejectWithValue, getState }) => {
        try {
            let { order } = request.dataField;
            if (order == null || order === 0) {
                const existingLayerDataFields = getState().mapReducer.mapModule.layers.value.find((l) => l.id === request.layerId)?.dataFields;
                order = (existingLayerDataFields?.length ?? 0) + 1;
            }

            incrementMonitoringCount({ name: MetricName.CREATE_DATAFIELD, resourceId: request.dataField.id });
            const response = await postLayerDataField({ ...request, dataField: { ...request.dataField, order } });

            // If the datafield has a default value we need to inform the map engine so when we create a map object it is reflected in the datafield values
            if (doesDataFieldHaveDefaultValue(request.dataField)) {
                eventStream.next({
                    type: "DATAFIELD_DEFAULT_VALUE_CHANGED",
                    payload: {
                        layerId: request.layerId,
                        existingDataField: null,
                        newDataField: request.dataField,
                    },
                });
            }

            return response;
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            return rejectWithValue(err.response.data);
        }
    }
);

export const updateLayerDataFieldThunk = createAsyncThunk<DataField, { dataField: DataField; layerId: string; previousDataField?: DataField }, { state: RootState }>(
    "Map/updateLayerDataField",
    async (request, { rejectWithValue }) => {
        try {
            // Use the pre-dispatch value captured by the caller to avoid pending-state optimistic updates.
            const hasDefaultValueChange = request.dataField.defaultValue !== request.previousDataField?.defaultValue;
            const previousValueMode = request.previousDataField?.valueMode ?? DataFieldValueMode.Manual;
            const nextValueMode = request.dataField.valueMode ?? DataFieldValueMode.Manual;
            const response = await mappingApi.patch<DataField>(`/layers/${request.layerId}/data-fields/${request.dataField.id}`, request.dataField, {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.layer_data_field) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.layer_data_field) }),
                },
            });

            if (previousValueMode !== nextValueMode) {
                incrementMonitoringCount({
                    name: nextValueMode === DataFieldValueMode.Calculated ? MetricName.LAYER_DATAFIELD_SET_TO_CALCULATED : MetricName.LAYER_DATAFIELD_SET_TO_MANUAL,
                    resourceId: request.dataField.id,
                });
            }

            const hasFormulaChange = !isEqual(request.previousDataField?.formula ?? [], request.dataField.formula ?? []);
            if (nextValueMode === DataFieldValueMode.Calculated && hasFormulaChange) {
                incrementMonitoringCount({
                    name: MetricName.LAYER_DATAFIELD_FORMULA_EDITED,
                    resourceId: request.dataField.id,
                });
            }

            if (hasDefaultValueChange) {
                eventStream.next({
                    type: "DATAFIELD_DEFAULT_VALUE_CHANGED",
                    payload: {
                        layerId: request.layerId,
                        existingDataField: request.previousDataField,
                        newDataField: request.dataField,
                    },
                });
            }

            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteLayerDataFieldThunk = createAsyncThunk<string, { dataFieldId: string; layerId: string }, { state: RootState }>(
    "Map/deleteLayerDataField",
    async (request, { rejectWithValue }) => {
        try {
            const response = await deleteLayerDataField(request);
            incrementMonitoringCount({ name: MetricName.DELETE_DATAFIELD, resourceId: request.dataFieldId });
            return response;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export interface GetGeoJsonResponse {
    feature: TypedFeature;
    objectId: string;
    layerId: string;
    waypoints: RouteWaypoint[] | undefined;
}

export const getGeoJson = createAsyncThunk<GetGeoJsonResponse[], { objects: { layerId: string; objectId: string }[]; mapId: string; layers: DomainLayer[] }>(
    "map/getGeoJson",
    async ({ objects, mapId, layers }, { rejectWithValue }) => {
        try {
            const objectsWithGeoJson = await Promise.all<GetGeoJsonResponse>(
                objects.map((object) =>
                    mappingApi.get<TypedFeature>(`maps/${mapId}/map_objects/${object.objectId}/geojson`).then((response) => {
                        const filteredLayers = layers.find((l) => l.id === object.layerId);
                        return {
                            feature: {
                                type: "Feature",
                                properties: convertDateDatafieldValuesToLocal({ ...response.data.properties, layerid: object.layerId }, filteredLayers?.dataFields),
                                geometry: response.data.geometry,
                            } as TypedFeature,
                            objectId: object.objectId,
                            layerId: object.layerId,
                            waypoints: undefined,
                        };
                    })
                )
            );
            // Assign their waypoints if they are routes
            for (let i = 0; i < objectsWithGeoJson.length; i += 1) {
                const object = objectsWithGeoJson[i];
                if (object.feature.properties.modeOfTransport != null) {
                    object.waypoints = await routeWaypointService.getRouteWaypoints(object.objectId, mapId);
                } else {
                    object.waypoints = undefined;
                }
            }

            return objectsWithGeoJson;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

// eslint-disable-next-line consistent-return
export const createObjects = createAsyncThunk<{ mapObjects: ObjectUpdate[] }, ObjectUpdate[], { state: RootState; rejectValue: ResponseError }>(
    "map/createObject",
    async (objects, { rejectWithValue, getState }) => {
        try {
            const { mapId, layers } = getState().mapReducer.mapModule;

            /** Maps a layer id to its layer to prevent repeated looping through layers */
            const layerIdToLayerMapping = new Map<string, MapLayer>();

            const mapObjects = objects.map((object) => {
                if (!layerIdToLayerMapping.has(object.layerId)) {
                    const layer = layers?.value?.find((l) => l.id === object.layerId);
                    layerIdToLayerMapping.set(object.layerId, layer);
                }
                const layerDataFields = layerIdToLayerMapping.get(object.layerId)?.dataFields ?? [];
                // Derive the data field values from the layer data fields in case the object has out of date data field values
                let dataFieldValues = layerDataFields.reduce<Record<string, unknown>>((acc, dataField) => {
                    if (object.properties[dataField.id] != null) {
                        acc[dataField.id] = object.properties[dataField.id];
                    }
                    return acc;
                }, {});
                // Ensure all time-related data field values are in UTC
                dataFieldValues = convertDateDatafieldValuesToUTC(dataFieldValues, layerDataFields);
                return {
                    id: object.objectId,
                    layerId: object.layerId,
                    geoJsonFeature: { type: "Feature", geometry: object.geometry, properties: object.properties },
                    dataFieldValues,
                    fixedShape: object.properties.fixedShape,
                    level: object.properties.level,
                    modeOfTransport: object.properties.modeOfTransport,
                    sitemapId: object.properties.sitemapId,
                } as MapObject;
            });
            await mappingApi.post<MapObject[]>(`/maps/${mapId}/map_objects`, mapObjects, { suppressToasts: true });

            // Update the waypoints if any of the objects are routes
            objects.forEach(async (update) => {
                if (update.waypoints != null && update.waypoints.length > 0) {
                    await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
                }
            });

            return { mapObjects: objects };
        } catch (err) {
            if (isAxiosError<ResponseError>(err)) {
                return rejectWithValue(err.response?.data ?? { message: "Unknown error" });
            }

            return rejectWithValue({ message: "Unknown error" });
        }
    }
);

export const restoreObjects = createAsyncThunk<{ mapObjects: MapObject[] }, ObjectUpdate[], { state: RootState; rejectValue: ResponseError }>(
    "map/restoreObjects",
    async (objects: ObjectUpdate[], { rejectWithValue, getState }) => {
        try {
            const { mapId, layers } = getState().mapReducer.mapModule;
            /** Maps a layer id to its layer to prevent repeated looping through layers */
            const layerIdToLayerMapping = new Map<string, MapLayer>();

            const mapObjects = objects.map((object) => {
                if (!layerIdToLayerMapping.has(object.layerId)) {
                    const layer = layers?.value?.find((l) => l.id === object.layerId);
                    layerIdToLayerMapping.set(object.layerId, layer);
                }
                const layer = layerIdToLayerMapping.get(object.layerId);
                return {
                    id: object.objectId,
                    layerId: object.layerId,
                    geoJsonFeature: {
                        type: object.geometry.type,
                        coordinates: object.geometry.coordinates,
                        rotation: object.properties.rotation,
                        properties: object.properties,
                    },
                    dataFieldValues: convertDateDatafieldValuesToUTC(uuidOnlyObjectEntries(object.properties), layer?.dataFields),
                    fixedShape: object.properties.fixedShape,
                    level: object.properties.level,
                    modeOfTransport: object.properties.modeOfTransport,
                } as MapObject;
            });

            await mappingApi.put<string[]>(
                `/maps/${mapId}/map_objects/restore`,
                mapObjects.map((o) => o.id),
                {
                    customErrorMessage: {
                        title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_objects) }),
                        message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_objects) }),
                    },
                }
            );

            // Update the waypoints if any of the objects are routes
            objects.forEach(async (update) => {
                if (update.waypoints != null && update.waypoints.length > 0) {
                    await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
                }
            });

            return { mapObjects };
        } catch (err: unknown) {
            if (isAxiosError<ResponseError>(err)) {
                return rejectWithValue(err.response?.data ?? { message: "Unknown error" });
            }

            return rejectWithValue({ message: "Unknown error" });
        }
    }
);

export const deleteObjects = createAsyncThunk<string[], { mapObjects: SelectedMapObject[]; modifyStore: boolean }, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/deleteObjects",
    async ({ mapObjects }, { rejectWithValue, dispatch, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;
            const objectIds = mapObjects.map((object) => object.objectId);
            dispatch(map.actions.addObjectChangesSaving(objectIds));
            // Construct a filter for mapObjectIds
            const filters = [
                {
                    $type: "IventisFilterGuidArray",
                    fieldName: "mapObjectIds",
                    operator: IventisFilterOperator.In,
                    value: objectIds,
                },
            ];
            await mappingApi.delete(`/maps/${mapId}/map_objects`, {
                data: filters,
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.map9.map_objects) }),
                    message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.map9.map_objects) }),
                },
            });
            dispatch(map.actions.removeObjectChangesSaving(objectIds));
            return objectIds;
        } catch (err) {
            console.error(err);
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteObjectsByLayerIds = createAsyncThunk<string[], { layerIds: string[] }, { getState; state: { mapReducer: MapGlobalState } }>(
    "map/deleteObjectsByLayerId",
    async ({ layerIds }, { rejectWithValue, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;
            // Construct a filter for layerIds
            const filters = [
                {
                    $type: "IventisFilterGuidArray",
                    fieldName: "layerIds",
                    operator: IventisFilterOperator.In,
                    value: layerIds,
                },
            ];
            await mappingApi.delete(`/maps/${mapId}/map_objects`, {
                data: filters,
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.map9.map_objects) }),
                    message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.map9.map_objects) }),
                },
            });
            return layerIds;
        } catch (err) {
            console.error(err);
            return rejectWithValue(err.response.data);
        }
    }
);

export const patchMapObjectsDataFieldValues = createAsyncThunk<
    MapObject[],
    {
        mapObjects: OptionalExceptFor<MapObject, "id" | "layerId">[];
        signal?: AbortSignal;
        from?: "data-table" | undefined;
        fulfilledNotification?: boolean;
        skipReflectLocalSource?: boolean;
    },
    { state: RootState }
>("map/patchMapObjectsDataFieldValues", async ({ mapObjects, signal }, { rejectWithValue, getState }) => {
    try {
        if (mapObjects?.length === 0 || mapObjects === undefined) {
            return [];
        }
        const { mapId, layers } = getState().mapReducer.mapModule;

        // Convert map object dates to UTC
        const mapObjectsPatch = mapObjects.map((object) => {
            const layer = layers?.value?.find((l) => l.id === object.layerId);

            // If repeated time range values are present they need to be converted, if value is not repeated time range it will be returned as is
            const objDF = parseDataFieldValuesWithRepeatedTimeRange(object?.dataFieldValues);

            return {
                ...object,
                dataFieldValues: convertDateDatafieldValuesToUTC(objDF, layer?.dataFields),
            };
        });
        const response = await mappingApi.patch<MapObject[]>(`/maps/${mapId}/map_objects/data-fields`, mapObjectsPatch, {
            signal,
            suppressErrors: true,
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_objects_attribute_values) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_objects_attribute_values) }),
            },
        });

        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const uploadGeometry = createAsyncThunk<{}, ObjectUpdate[], { getState; state: { mapReducer: MapGlobalState } }>("map/uploadGeometry", async (objects, { getState }) => {
    const {
        mapModule: { mapId },
        creatingObjects,
    } = getState().mapReducer;

    const objectsToUpdateNow = objects.filter((o) => !creatingObjects.includes(o.objectId));
    // Only upload geometries if the object has already been created, if it hasn't the middleware will handle the queue
    if (objectsToUpdateNow.length > 0) {
        await mappingApi.put<void, AxiosResponse<void>, UpdateMultipleMapObjects>(
            `maps/${mapId}/map_objects/geometry`,
            objectsToUpdateNow.reduce(
                (cum, object) => {
                    const add = () => ({
                        id: object.objectId,
                        type: object.geometry.type,
                        coordinates: object.geometry.coordinates,
                        rotation: object.properties.rotation,
                        level: object.properties.level,
                        modeOfTransport: object.properties.modeOfTransport,
                        sitemapId: object.properties.sitemapId,
                    });
                    switch (object.geometry.type) {
                        case GeometryType.LineString:
                            return { ...cum, lineStrings: [...cum.lineStrings, add()] };
                        case GeometryType.Polygon: {
                            return { ...cum, polygons: [...cum.polygons, add()] };
                        }
                        case GeometryType.Point:
                            return { ...cum, points: [...cum.points, add()] };
                        default:
                            return cum;
                    }
                },
                { lineStrings: [], polygons: [], points: [] }
            ),
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_object_geometry) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_object_geometry) }),
                },
            }
        );

        // Update the waypoints if any of the objects are routes
        objectsToUpdateNow.forEach(async (update) => {
            if (update.properties.modeOfTransport != null) {
                await routeWaypointService.putRouteWaypoints(update.objectId, mapId, update.waypoints);
            }
        });
    }
});

export const getProjectDataFields = createAsyncThunk<DataField[], { signal: AbortSignal }, { state: RootState }>("map/getProjectDataFields", async ({ signal }, { getState }) => {
    const { projectId } = getState().auth.user;
    const dataFields = await mappingApi.get<DataField[]>(`/projects/${projectId}/data-fields`, { signal });
    return dataFields.data;
});

export const getSitemaps = createAsyncThunk<Sitemap[], { signal?: AbortSignal }, { state: RootState }>("map/getSitemaps", async ({ signal }, { getState }) => {
    if (!getState().auth.subscriptionPlan.functionality.siteMaps) {
        return [];
    }

    const sitemaps = await mappingApi.get<Sitemap[]>(`/sitemaps`, {
        signal,
        params: {
            filter: JSON.stringify([{ fieldName: "status", operator: IventisFilterOperator.In, value: ["Active"] }]),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });
    return sitemaps.data;
});

export const getTerrains = createAsyncThunk<Terrain[], { signal?: AbortSignal }, { state: RootState }>("map/getTerrains", async ({ signal }, { dispatch }) => {
    const terrains = await mappingApi.get<Terrain[]>(`/terrains`, {
        signal,
        params: {
            filter: JSON.stringify([{ fieldName: "status", operator: IventisFilterOperator.In, value: ["Active"] }]),
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });

    dispatch(map.actions.setTerrains(terrains.data));
    dispatch(getTerrainAssets(terrains.data));
    return terrains.data;
});

export const getTerrainAssets = createAsyncThunk<Asset[], Terrain[], { state: RootState }>("map/getTerrainAssets", async (terrains: Terrain[]) => {
    const terrainAssets: Asset[] = [];
    await Promise.all(
        terrains.map(async (terrain: Terrain) => {
            // Get the terrain asset
            const terrainAsset = await assetsApi.get<Asset>(`/assets/${terrain.id}`);
            terrainAssets.push(terrainAsset.data);
        })
    );
    return terrainAssets;
});

export const updateTerrainSignatures = createAsyncThunk<Asset[], Terrain[], { state: RootState }>("map/updateTerrainSignatures", async (terrains, { getState }) => {
    if (!getState().auth.subscriptionPlan.functionality.siteMaps) {
        return [];
    }
    const assets: Asset[] = [];
    await Promise.all(
        terrains.map(async (terrain) => {
            // Get the terrain asset
            const terrainAsset = await assetsApi.get<Asset>(`/assets/${terrain.id}`);
            if (terrainAsset?.data != null) {
                terrainAsset.data.updatedAt = parseISO(terrainAsset.data.updatedAt.toString());
            }
            assets.push(terrainAsset.data);
        })
    );
    return assets;
});

export const updateSavedMapView = createAsyncThunk("Map/updateSavedMapView", async (savedMapView: SavedMapView, { rejectWithValue }) => {
    try {
        const response = await mappingApi.put<SavedMapView>(`/saved_map_views`, savedMapView, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.saved_map_view) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.saved_map_view) }),
            },
        });
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const createSavedMapView = createAsyncThunk("Map/createSavedMapView", async (savedMapView: SavedMapView, { rejectWithValue }) => {
    try {
        const response = await mappingApi.post<SavedMapView>(`/saved_map_views`, savedMapView, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map9.saved_map_view) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map9.saved_map_view) }),
            },
        });
        return response.data;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const patchMapBackgroundId = createAsyncThunk("Map/patchMapBackgroundId", async (data: { mapId: string; backgroundId: string }, { rejectWithValue }) => {
    try {
        await mappingApi.patch<Map>(
            `/maps/${data.mapId}`,
            { backgroundId: data.backgroundId },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_background) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_background) }),
                },
            }
        );
        return data.backgroundId;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const patchDefaultSavedMapView = createAsyncThunk("Map/patchDefaultSavedMapView", async (data: { mapId: string; savedMapViewId: string }, { rejectWithValue }) => {
    try {
        await mappingApi.patch<Map>(
            `/maps/${data.mapId}`,
            { defaultSavedMapViewId: data.savedMapViewId },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.saved_map_view) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.saved_map_view) }),
                },
            }
        );
        return data.savedMapViewId;
    } catch (err) {
        const error: AxiosError<ResponseError> = err;
        // eslint-disable-next-line no-console
        console.error("Error", error);
        return rejectWithValue(error.response.data);
    }
});

export const getRemoteLayerGeometry = createAsyncThunk<LocalGeoJsonObject[], { layerId: string; signal?: AbortSignal; overwrite?: boolean }, { state: RootState }>(
    "Map/getRemoteLayerGeometry",
    async ({ layerId, signal }, { rejectWithValue, getState }) => {
        try {
            const { mapId } = getState().mapReducer.mapModule;
            const filter = {
                params: {
                    filter: JSON.stringify([{ fieldName: "layerId", operator: IventisFilterOperator.In, value: [layerId] }]),
                    mapId,
                },
                paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
            };
            const res = await aggregatePagedResponse<MapObject[]>(
                mappingApi,
                `/maps/${getState().mapReducer.mapModule.mapId}/map_objects/filter`,
                { ...filter, signal },
                async (cum, newValue) => (cum ?? []).concat(newValue)
            );
            // Get the waypoints for the map objects if they exist
            const waypoints = await Promise.all(
                res.map<Promise<RouteWaypoint[]>>(async (object) => {
                    let waypoints: RouteWaypoint[] | null = null;
                    if ((object.modeOfTransport ?? object.geoJsonFeature?.properties?.modeOfTransport) != null) {
                        waypoints = await routeWaypointService.getRouteWaypoints(object.id, mapId);
                    }
                    return waypoints;
                })
            );
            return res?.map?.((object, index) => convertMapObjectToLocalGeoJson(object, waypoints[index]));
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const updateMapSidebarOrder = createAsyncThunk("Map/updateLayersSidebarOrder", async (data: { layers: SidebarOrderUpdate[]; mapId: string }, { rejectWithValue }) => {
    try {
        const request = await mappingApi.patch(`maps/${data.mapId}/layers`, data.layers, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_sidebar_order) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_sidebar_order) }),
            },
        });
        return request;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const patchMapComment = createAsyncThunk<void, GeoJSON.Feature<GeoJSON.Point, MapObjectProperties>, { state: RootState }>(
    "Map/patchMapComment",
    async (payload, { getState }) => {
        const { mapId } = getState().mapReducer.mapModule;
        await mappingApi.patch<Partial<MapComment>>(
            `/maps/${mapId}/comments/${payload.properties.id}`,
            {
                geometry: payload.geometry,
                level: payload.properties.level,
                sitemapId: payload.properties.sitemapId,
            },
            {
                customErrorMessage: {
                    title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_comment) }),
                    message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_comment) }),
                },
            }
        );
    }
);

export const getMapCommentFeature = createAsyncThunk<
    GeoJSON.Feature<GeoJSON.Point, MapCommentProperties>,
    { commentId: string; focus?: boolean; panTo?: boolean; existsOnServer?: boolean },
    { state: RootState }
>("Map/getMapComment", async ({ commentId, focus }, { getState }) => {
    const { mapId, geoJSON } = getState().mapReducer.mapModule;
    const existingComment = geoJSON.value[commentsLayerId]?.find((c) => c.objectId === commentId);
    if (existingComment != null) {
        return {
            ...existingComment.feature,
            properties: { ...existingComment.feature.properties, imageId: focus ? DEFAULT_COMMENT_ICON_VALUE : existingComment.feature.properties.userId },
        } as GeoJSON.Feature<GeoJSON.Point, MapCommentProperties>;
    }
    const response = await mappingApi.get<MapComment>(`maps/${mapId}/comments/${commentId}`);
    const feature = {
        geometry: response.data.geometry,
        type: "Feature",
        properties: {
            id: response.data.id,
            layerid: commentsLayerId,
            level: response.data.level,
            userId: response.data.userId,
            imageId:
                // Ensure the selection hasn't changed since the request was made
                focus && getState().mapReducer.mapModule.commentsSelected.value.some((c) => c.commentId === response.data.id) ? DEFAULT_COMMENT_ICON_VALUE : response.data.userId,
            rotation: undefined,
            order: focus ? 1 : 0,
        },
    } as const;
    return feature;
});

export const updateMapObject = createAsyncThunk<void, { mapObjectPatch: OptionalExceptFor<MapObject, "id">; mapId: string; layerId: string }, { state: RootState }>(
    "Map/updateMapObject",
    async ({ mapObjectPatch, mapId }) => {
        await mappingApi.patch<Partial<MapObject>>(`/maps/${mapId}/map_objects/${mapObjectPatch.id}`, mapObjectPatch, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map9.map_object) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map9.map_object) }),
            },
        });
    }
);

export const setUserConnectedToDigitalTwin = createAsyncThunk<void, { connected: boolean }, { state: RootState }>("map/setUserConnectedToDigitalTwin", async (_, { getState }) => {
    const { projectId } = getState().auth.user;
    const { id: licenseId } = getState().mapReducer.mapModule.digitalTwinEngineData.currentDigitalTwinLicence;
    await mappingApi.patch(`/projects/${projectId}/digital-twin/${licenseId}/user`);
});

export const connectToDigitalTwinLicense = createAsyncThunk<{ data: DigitalTwinLicence | null; error?: DigitalTwinConnectionError }, void, { state: RootState }>(
    "map/connectToDigitalTwinLicense",
    async (_, { getState }) => {
        const { projectId } = getState().auth.user;

        try {
            const repsonse = await mappingApi.post<DigitalTwinLicence>(`/projects/${projectId}/digital-twin`, null, { suppressToasts: true });
            return { data: repsonse.data };
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                switch (error.response.status) {
                    // No licenece
                    case 400:
                        return { data: null, error: "occupied" };
                    // Default errors 500 etc.
                    default:
                        return { data: null, error: "other" };
                }
            }
        }

        return null;
    }
);

export const getRelayTimetableConfig = createAsyncThunk<RelayTimetableConfig | null, { relayId: string; dayId: string; mapId: string } | null>(
    "map/getRelayTimetableConfig",
    async (location) => {
        if (location == null) {
            return null;
        }

        const relayDay = await createRelayTimetableApiFunctions(mappingApi, assetsApi).getRelayDayForMap(location.relayId, location.dayId, location.mapId);
        return relayDay?.relay.timetableConfig ?? null;
    }
);
